import { getAllOverrides } from "@/lib/copyOverrides";

const ENDPOINT =
  "https://jhgvpqnesquocndbzygl.supabase.co/functions/v1/promo-events";
const SITE = "sons-rio-cibolo";

type Surface = "banner" | "popup";
type EventName = "view" | "click";

/** True when analytics should be skipped (preview flag, localhost, Lovable hosts). */
function shouldSkip(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has("promo_preview")) return true;
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1" || host.includes("lovable")) {
      return true;
    }
  } catch {
    return true;
  }
  return false;
}

async function surfaceHash(surface: Surface): Promise<string> {
  const all = getAllOverrides();
  const values: Record<string, string> = {};
  for (const [k, v] of Object.entries(all)) {
    if (k.startsWith(`${surface}.`)) values[k] = v;
  }
  const canonical = Object.keys(values).length
    ? JSON.stringify(
        Object.fromEntries(
          Object.entries(values).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
        ),
      )
    : "{}";
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(canonical),
  );
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Fire-and-forget beacon. Never throws, never logs. */
export function sendPromoEvent(surface: Surface, event: EventName): void {
  if (shouldSkip()) return;
  try {
    void (async () => {
      try {
        const body = JSON.stringify({
          site: SITE,
          surface,
          event,
          hash: await surfaceHash(surface),
          path: window.location.pathname,
          ts: Date.now(),
        });
        const blob = new Blob([body], { type: "application/json" });
        if (!navigator.sendBeacon?.(ENDPOINT, blob)) {
          void fetch(ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: true,
          }).catch(() => {});
        }
      } catch {
        /* ignore */
      }
    })();
  } catch {
    /* ignore */
  }
}

const seenViews = new Set<string>();

/** One `view` per page load per pathname for a surface. */
export function sendPromoViewOnce(surface: Surface, pathname: string): void {
  const key = `${surface}:${pathname}`;
  if (seenViews.has(key)) return;
  seenViews.add(key);
  sendPromoEvent(surface, "view");
}
